import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../../../components/ui/dropdown-menu'
import { monthKey } from '../../hooks/upcomingTasks.utils'
import './MonthTitle.scss'

export interface MonthTitleProps {
  month: Date
  /** Visual scale. `lg` (default) is the pinned page header; `sm` is the
   *  inline section header rendered above each MonthBlock. */
  size?: 'lg' | 'sm'
  /** When false, hides the year span. Defaults to true for the pinned header. */
  showYear?: boolean
  /** When provided, the year becomes a dropdown trigger: clicking it opens a
   *  year picker (±5 years around the current year) and reports the chosen
   *  year. Only makes sense when `showYear` is true. */
  onYearSelect?: (year: number) => void
}

/**
 * Two-color month header: bold month name in `--space-950` + regular year in
 * `--foreground-mid`. Cross-fades between months so the pinned header tracks
 * scroll smoothly like an iOS Contacts section header.
 */
export const MonthTitle: React.FC<MonthTitleProps> = ({
  month,
  size = 'lg',
  showYear = true,
  onYearSelect,
}) => {
  const { i18n } = useTranslation()
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : 'en-US'

  const monthName = month.toLocaleDateString(locale, { month: 'long' })
  const year = month.getFullYear()
  const key = monthKey(month)
  const className = `month-title month-title--${size}`

  /*
    **Platzhalter für die Breite** (25.09.2026): Der Inhalt steht absolut, damit
    alter und neuer Monat übereinander überblenden können — der Kasten selbst
    hätte sonst keine Breite. Mit fester Mindestbreite lief „September 2026“ im
    schmalen Fenster in die Monatsleiste daneben. Der Platzhalter trägt den
    LÄNGSTEN Monatsnamen der Sprache, unsichtbar; so ist der Titel immer so
    breit wie sein breitester Text, und schrumpfen muss die Leiste.
  */
  const longestMonthName = React.useMemo(() => {
    const names = Array.from({ length: 12 }, (_, m) =>
      new Date(2000, m, 1).toLocaleDateString(locale, { month: 'long' }),
    )
    return names.reduce((a, b) => (b.length > a.length ? b : a))
  }, [locale])

  const yearOptions = React.useMemo(() => {
    return Array.from({ length: 11 }, (_, i) => year - 5 + i)
  }, [year])

  const yearContent = showYear ? (
    onYearSelect ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="month-title__year month-title__year-button">
            <span>{year}</span>
            <ChevronDown size={14} strokeWidth={2.25} className="month-title__year-chevron" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {yearOptions.map(y => (
            <DropdownMenuItem
              key={y}
              onSelect={() => onYearSelect(y)}
              className={y === year ? 'month-title__year-option--current' : undefined}
            >
              {y}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <span className="month-title__year">{year}</span>
    )
  ) : null

  return (
    <div className={className} aria-live="polite">
      {size === 'lg' && (
        <span className="month-title__sizer" aria-hidden="true">
          <span className="month-title__name">{longestMonthName}</span>
          {showYear && (
            <span className={`month-title__year${onYearSelect ? ' month-title__year-button' : ''}`}>
              <span>{year}</span>
              {onYearSelect && <ChevronDown size={14} strokeWidth={2.25} className="month-title__year-chevron" />}
            </span>
          )}
        </span>
      )}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={key}
          className="month-title__inner"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          <span className="month-title__name">{monthName}</span>
          {yearContent}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
